import { ExternalLink, GoogleAuthenticator, LaptopApps, Success } from '@nosinovacao/fe-icons';
import { Button, cn, If, QrCode, useI18n } from '@nosinovacao/nosid-mfe-common';
import { type ReactNode } from 'react';
import { type AuthenticatorApp } from '../../../../-model/flow';
import { type DownloadAppProps } from '../-model/props';

const KEY_PREFIX = 'Security.AuthenticatorAppCard.Setup.DownloadApp';

const otherApps = [
  `${KEY_PREFIX}.Other.Item1`,
  `${KEY_PREFIX}.Other.Item2`,
  `${KEY_PREFIX}.Other.Item3`,
];

const storeUrlKey = {
  android: `${KEY_PREFIX}.Google.UrlAndroid`,
  ios: `${KEY_PREFIX}.Google.UrlIos`,
} as const;

export function DownloadAppLight({
  goToRegister,
  isApple,
  openQrCode,
  platform,
  qrCodeOpen,
  select,
  selected,
}: Readonly<DownloadAppProps>) {
  const { t } = useI18n();
  const isMobile = platform !== null;
  const qrCodeStore = isApple ? 'ios' : 'android';

  if (qrCodeOpen) {
    return (
      <>
        <div className="grid flex-1 auto-rows-max content-start gap-6 overflow-y-auto">
          <h1 data-tid="header-title" className="font-bold text-nos-neutral-1000 font-title-h2">
            {t(`${KEY_PREFIX}.Google.QrCode.Title`)}
          </h1>
          <QrCode
            skin="light"
            size={207}
            value={t(storeUrlKey[qrCodeStore]).toString()}
            className="mx-auto w-fit"
          />
          <p className="text-nos-neutral-800 font-text-p2">
            {t(
              isApple
                ? `${KEY_PREFIX}.Google.QrCode.DescriptionIos`
                : `${KEY_PREFIX}.Google.QrCode.DescriptionAndroid`,
            )}
          </p>
        </div>
        <ContinueButton isMobile={isMobile} disabled={!selected} onClick={goToRegister} />
      </>
    );
  }

  return (
    <>
      <div className="grid flex-1 auto-rows-max content-start gap-2 overflow-y-auto">
        <AppCard
          app="google"
          selected={selected === 'google'}
          select={select}
          testId="google-authenticator-card"
          icon={
            <GoogleAuthenticator
              width={32}
              height={32}
              className="rounded-sm border border-nos-neutral-200"
            />
          }
          title={t(`${KEY_PREFIX}.Google.Title`)}
          subtitle={t(`${KEY_PREFIX}.Google.Subtitle`)}
          subtitleClassName="text-nos-blue"
        >
          {platform ? (
            <Button
              skin="light"
              variant="secondary"
              asChild
              data-tid="google-authenticator-download-button"
              className="relative w-fit px-4"
            >
              <a href={t(storeUrlKey[platform]).toString()} target="_blank" rel="noreferrer">
                {t(`${KEY_PREFIX}.Google.DownloadMobile`)}
              </a>
            </Button>
          ) : (
            <Button
              skin="light"
              variant="secondary"
              type="button"
              onClick={openQrCode}
              data-tid="google-authenticator-download-button"
              className="relative w-fit px-4"
            >
              {t(`${KEY_PREFIX}.Google.DownloadDesktop`)}
              <ExternalLink width={16} height={16} />
            </Button>
          )}
        </AppCard>

        <AppCard
          app="other"
          selected={selected === 'other'}
          select={select}
          testId="other-app-card"
          icon={<LaptopApps width={32} height={32} className="text-nos-neutral-900" />}
          title={t(`${KEY_PREFIX}.Other.Title`)}
          subtitle={t(`${KEY_PREFIX}.Other.Subtitle`)}
        >
          <div className="grid gap-1 text-nos-neutral-800 font-ui-9">
            <span>{t(`${KEY_PREFIX}.Other.ListTitle`)}</span>
            <ul className="list-disc pl-5">
              {otherApps.map((app) => (
                <li key={app}>{t(app)}</li>
              ))}
            </ul>
          </div>
        </AppCard>
      </div>

      <ContinueButton isMobile={isMobile} disabled={!selected} onClick={goToRegister} />
    </>
  );
}

function ContinueButton({
  disabled,
  isMobile,
  onClick,
}: Readonly<{ disabled: boolean; isMobile: boolean; onClick: () => void }>) {
  const { t } = useI18n();
  return (
    <footer className="grid pt-2">
      <Button
        skin="light"
        type="button"
        disabled={disabled}
        onClick={onClick}
        data-tid="continue-button"
        className="w-full px-4 tablet:w-fit tablet:justify-self-start"
      >
        {t(isMobile ? `${KEY_PREFIX}.ContinueMobile` : `${KEY_PREFIX}.ContinueDesktop`)}
      </Button>
    </footer>
  );
}

function AppCard({
  app,
  children,
  icon,
  select,
  selected,
  subtitle,
  subtitleClassName,
  testId,
  title,
}: Readonly<{
  app: AuthenticatorApp;
  children: ReactNode;
  icon: ReactNode;
  select: (app: AuthenticatorApp) => void;
  selected: boolean;
  subtitle: ReactNode;
  subtitleClassName?: string;
  testId: string;
  title: ReactNode;
}>) {
  return (
    <div
      className={cn(
        'relative grid gap-4 rounded-lg bg-nos-neutral-0 p-4',
        selected ? 'border-2 border-nos-green' : 'border border-nos-neutral-200',
      )}
    >
      <If condition={selected}>
        <Success
          width={24}
          height={24}
          className="absolute right-4 top-4 text-nos-green"
          aria-hidden="true"
        />
      </If>
      {/* the ::after overlay makes the whole card select the app while keeping the download link clickable */}
      <button
        type="button"
        aria-pressed={selected}
        onClick={() => select(app)}
        data-tid={testId}
        className="grid grid-cols-[auto,1fr] items-center gap-x-3 pr-8 text-left after:absolute after:inset-0 after:rounded-lg after:content-['']"
      >
        {icon}
        <span className="grid">
          <span className="font-bold text-nos-neutral-900 font-ui-8">{title}</span>
          <span className={cn('text-nos-neutral-800 font-ui-9', subtitleClassName)}>
            {subtitle}
          </span>
        </span>
      </button>
      {children}
    </div>
  );
}
